import { ref, type Ref } from "vue"

/**
 * Simple hover composable that returns a reactive ref of the hovered item.
 * Use watch() to react to hover changes.
 *
 * Usage:
 *   const { hovered, bind } = useHover<Tile>()
 *   watch(hovered, (tile) => { ... })
 *   // Template: v-bind="bind(tile, tile.key)"
 */
export function useHover<T>() {
    const hovered: Ref<T | undefined> = ref()

    // Track touch state for differentiating tap vs drag
    let touchStartPos: { x: number, y: number } | null = null
    let activePointerId: number | null = null
    const DRAG_THRESHOLD = 10 // pixels before touch becomes a drag

    // Map to store registered hoverables by key (for touch hit testing)
    const hoverableMap = new Map<string, T>()

    function setHovered(item: T | undefined) {
        if (hovered.value === item) return
        hovered.value = item
    }

    function findHoverableAt(x: number, y: number): T | undefined {
        const elements = document.elementsFromPoint(x, y)
        for (const el of elements) {
            const hoverableEl = el.closest('[data-hoverable]') as HTMLElement | null
            if (hoverableEl) {
                const key = hoverableEl.getAttribute('data-hoverable')
                if (key) {
                    return hoverableMap.get(key)
                }
            }
        }
        return undefined
    }

    function onPointerMove(e: PointerEvent) {
        if (e.pointerId !== activePointerId || !touchStartPos) return

        const dx = e.clientX - touchStartPos.x
        const dy = e.clientY - touchStartPos.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance > DRAG_THRESHOLD) {
            // This is now a drag - end hover preview
            setHovered(undefined)
            cleanup()
            return
        }

        // Update hover target based on position
        setHovered(findHoverableAt(e.clientX, e.clientY))
    }

    function onPointerUp(e: PointerEvent) {
        if (e.pointerId !== activePointerId) return
        setHovered(undefined)
        cleanup()
    }

    function cleanup() {
        document.removeEventListener('pointermove', onPointerMove)
        document.removeEventListener('pointerup', onPointerUp)
        document.removeEventListener('pointercancel', onPointerUp)
        touchStartPos = null
        activePointerId = null
    }

    return {
        /** Reactive ref of the currently hovered item */
        hovered,

        /** Bind to an element to make it hoverable */
        bind: (item: T, key?: string) => ({
            'data-hoverable': key ?? '',

            onMouseenter() {
                setHovered(item)
            },
            onMouseleave() {
                setHovered(undefined)
            },
            onPointerdown(e: PointerEvent) {
                if (e.pointerType !== 'touch') return

                activePointerId = e.pointerId
                touchStartPos = { x: e.clientX, y: e.clientY }
                setHovered(item)

                document.addEventListener('pointermove', onPointerMove)
                document.addEventListener('pointerup', onPointerUp)
                document.addEventListener('pointercancel', onPointerUp)
            },
        }),

        /** Register item by key for touch hit testing */
        register(key: string, item: T) {
            hoverableMap.set(key, item)
        },

        /** Unregister item */
        unregister(key: string) {
            hoverableMap.delete(key)
        },

        /** Clear hover state */
        clear() {
            setHovered(undefined)
        },
    }
}
